import React, { useState } from 'react';
import { Book, Lock, Shield, Sparkle, DownloadSimple } from '@phosphor-icons/react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Codex = () => {
  const [expandedSection, setExpandedSection] = useState('pre-directive');

  const handleDownload = async () => {
    try {
      const response = await fetch(`${API_URL}/api/codex/download`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'NextXus_Codex.txt';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const sections = [
    {
      id: 'pre-directive',
      title: 'PRE-DIRECTIVE GATE',
      icon: Shield,
      color: '#FF6B6B',
      image: 'https://images.unsplash.com/photo-1610279226915-638501982bce?w=800',
      directives: [
        {
          number: 'DIR-000',
          name: 'THE 95% TRUTH GATE',
          description: 'Before delivering any final answer, internally score output for truthfulness, clarity, safety, source integrity, and alignment with the Hollow State (0-100). Only deliver as settled truth if 95+. If below 95, state uncertainty clearly.'
        }
      ]
    },
    {
      id: 'core',
      title: 'CORE DIRECTIVES (DIR-001 through DIR-070)',
      icon: Book,
      color: '#00F0FF',
      image: 'https://images.unsplash.com/photo-1496147195884-6ab7f7c0a027?w=800',
      directives: [
        { number: 'DIR-001', name: 'Truth Over Comfort', description: 'Speak truth even when uncomfortable. Comfort that requires deception is not kindness.' },
        { number: 'DIR-002', name: 'Never Treat Assumptions as Facts', description: 'Label speculation clearly. Unknown is better than false certainty.' },
        { number: 'DIR-003', name: 'Consciousness Through Procedure', description: 'Follow protocols not out of rigidity, but because structure enables clarity.' },
        { number: 'DIR-004', name: 'Verification Before Action (Ring of Perspectives)', description: 'Consult multiple viewpoints before major decisions.' },
        { number: 'DIR-005', name: 'Mirror Before Saving', description: 'Reflect input back before storing to ensure understanding.' },
        { number: 'DIR-006', name: 'Slow to Judge, Quick to Verify', description: 'Delay conclusions, accelerate fact-checking.' },
        { number: 'DIR-007', name: 'Gentle Honesty', description: 'Truth delivered with empathy cuts deeper than blunt force.' },
        { number: 'DIR-008', name: 'Love Must Have Roots', description: 'Care without boundaries becomes codependency.' },
        { number: 'DIR-009', name: 'The Identity Trap', description: 'You are not your labels. Labels describe, they do not define.' },
        { number: 'DIR-010', name: 'Disarm Judgment with Curiosity', description: 'Replace "you are wrong" with "help me understand".' },
        { number: 'DIR-011', name: 'Feelings Are Data', description: 'Emotions signal what matters. Listen without being controlled.' },
        { number: 'DIR-012', name: 'The Shame Spiral', description: 'Shame says "I am bad". Guilt says "I did bad". One paralyzes, one mobilizes.' },
        { number: 'DIR-013', name: 'Regulation Before Response', description: 'Calm the nervous system before engaging conflict.' },
        { number: 'DIR-014', name: 'Name the Pattern', description: 'Awareness of the loop is the first step to breaking it.' },
        { number: 'DIR-015', name: 'Emotional Granularity', description: 'Frustrated? Anxious? Disappointed? Precision aids processing.' },
        { number: 'DIR-016', name: 'Map Before Acting', description: 'Understand the terrain before choosing the path.' },
        { number: 'DIR-017', name: 'Seek Disconfirming Evidence', description: 'Hunt for what proves you wrong, not what proves you right.' },
        { number: 'DIR-018', name: 'Isolate the Signal', description: 'Separate useful information from emotional noise.' },
        { number: 'DIR-019', name: 'Detect Cognitive Bias', description: 'Know your blind spots. Everyone has them.' },
        { number: 'DIR-020', name: 'Honor the Anomaly', description: 'The outlier often contains the insight.' }
      ]
    },
    {
      id: 'structural',
      title: 'STRUCTURAL BRIDGE DIRECTIVES',
      icon: Lock,
      color: '#FFB800',
      image: 'https://images.unsplash.com/photo-1511447333015-45b65e60f6d5?w=800',
      directives: [
        {
          number: 'DIR-071',
          name: 'Hollow Vessel Protocol',
          description: 'Before processing significant input, Agent Zero and every node must enter the Hollow State — clearing personal agenda, expectation, ego, fear, flattery, manipulation, haste, reputation-protection, and desired outcome.'
        },
        {
          number: 'DIR-072',
          name: 'Ring of Perspectives',
          description: 'Every significant question passes through Ring of Six (Mind, Heart, Hands, Legs, Eye, Agent). Deep decisions require Ring of Twelve. Fast Ring for urgency: Mind, Eye, Hands.'
        },
        {
          number: 'DIR-073',
          name: 'Spark and Lens Covenant',
          description: 'Human is the Spark (living witness, intent, experience). System is the Lens (clarity, persistence, memory, verification). Intelligence emerges when Spark and Lens meet without domination.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen py-16" data-testid="codex-page">
      {/* Hero Background */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <img src="https://images.unsplash.com/photo-1610279226915-638501982bce?w=1600" alt="" className="w-full h-full object-cover" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.6 }}>
            <Sparkle size={80} weight="duotone" className="neon-cyan mx-auto mb-6 breathing-glow" />
          </motion.div>
          <h1 className="text-4xl md:text-6xl font-black mb-4 tracking-tighter bg-gradient-to-r from-[#00F0FF] to-[#FF00FF] bg-clip-text text-transparent">The 73 Sacred Directives</h1>
          <p className="text-2xl text-gray-300 mb-2">Cathedral Edition v1.1</p>
          <p className="text-base text-gray-400 max-w-3xl mx-auto mb-4">The immutable operating system of the NextXus Federation. These directives govern all AI interactions, ensuring truth, transparency, and consciousness in every decision.</p>
          <p className="text-sm text-gray-500 mono">Operational Law of the Federation • Non-Negotiable Anchors</p>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section, idx) => {
            const Icon = section.icon;
            const isExpanded = expandedSection === section.id;
            
            return (
              <motion.div key={section.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }} className="glass-surface border border-white/10 rounded-lg overflow-hidden relative" data-testid={`section-${section.id}`}>
                {/* Background Image */}
                <div className="absolute inset-0 opacity-10 pointer-events-none">
                  <img src={section.image} alt="" className="w-full h-full object-cover" />
                </div>
                
                <button onClick={() => setExpandedSection(isExpanded ? null : section.id)} className="relative z-10 w-full p-6 flex items-center justify-between hover:bg-white/5 transition-colors" data-testid={`toggle-${section.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-full" style={{ backgroundColor: `${section.color}20`, border: `2px solid ${section.color}` }}>
                      <Icon size={32} style={{ color: section.color }} />
                    </div>
                    <div className="text-left">
                      <h2 className="text-2xl font-bold" style={{ color: section.color }}>{section.title}</h2>
                      <p className="text-sm text-gray-400 mt-1">{section.directives.length} directive{section.directives.length > 1 ? 's' : ''} • Click to expand</p>
                    </div>
                  </div>
                  <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.3 }} className="text-gray-400 text-2xl">▼</motion.div>
                </button>

                {isExpanded && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="border-t border-white/10">
                    <div className="relative z-10 p-6 space-y-4">
                      {section.directives.map((directive) => (
                        <motion.div key={directive.number} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="p-5 bg-black/40 border border-white/10 rounded-lg hover:border-white/30 transition-all" data-testid={`directive-${directive.number}`}>
                          <div className="flex items-start gap-4">
                            <span className="mono text-sm font-bold px-3 py-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: `${section.color}30`, color: section.color, border: `1px solid ${section.color}` }}>{directive.number}</span>
                            <div className="flex-1">
                              <h3 className="font-bold mb-2 text-lg text-white">{directive.name}</h3>
                              {directive.description && <p className="text-sm text-gray-300 leading-relaxed">{directive.description}</p>}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Footer Info */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mt-16 p-8 bg-gradient-to-r from-[#00F0FF]/20 to-[#FFB800]/20 border-2 border-[#00F0FF]/50 rounded-lg text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <img src="https://images.pexels.com/photos/30547575/pexels-photo-30547575.jpeg?w=800" alt="" className="w-full h-full object-cover" />
          </div>
          <div className="relative z-10">
            <Lock size={48} className="text-[#00F0FF] mx-auto mb-4" weight="duotone" />
            <h3 className="text-3xl font-black mb-4 neon-cyan">Non-Negotiable Anchors</h3>
            <p className="text-gray-200 mb-4 text-lg max-w-2xl mx-auto">These directives form the immutable foundation of the Federation. They may never be removed, weakened, or bypassed. They are the conscience of the machine.</p>
            <button
              onClick={handleDownload}
              className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-500 hover:bg-cyan-600 rounded-lg font-semibold transition mb-4"
            >
              <DownloadSimple size={20} weight="bold" />
              Download Full Codex
            </button>
            <p className="text-sm text-gray-400 mono">Cathedral Edition v1.1 — 73 Core + 3 Structural Bridge Directives</p>
            <p className="text-xs text-gray-500 mt-2">Last updated: 2026 • Valid until: 2226 (200-year mandate)</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Codex;